import React from 'react';
import PropTypes from 'prop-types';
import {
  TableCell, TableHead as MuiTableHead, TableRow, Stack,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from './Tooltip';

const TableHead = ({
  headCells,
  sortSelect = null,
  pagination = null,
  stickyHeader = false,
  backgroundColor = null,
  order,
  orderBy,
  onRequestSort,
  numSelected = 0,
  rowCount = 0,
  onSelectAllClick,
}) => (
  <MuiTableHead
    sx={{
      ...(stickyHeader && { position: 'sticky', top: 0, backgroundColor: 'white' }),
      ...(backgroundColor && !stickyHeader && { backgroundColor: 'white' }),
    }}
  >
    <TableRow>
      {!!sortSelect && (
      <TableCell variant="head">
        {sortSelect}
      </TableCell>
      )}
      {!!pagination && (
      <>
        {pagination}
      </>
      )}
    </TableRow>
    <TableRow
      sx={{
        height: '37px',
        pt: 0,
        pb: 0,
      }}
    >
      {headCells.map((headCell) => {
        const isActive = orderBy === headCell.id;
        const createSortHandler = (property) => (event) => {
          onRequestSort(event, property);
        };

        if (headCell.isCheckbox) {
          return (
            <TableCell
              key={headCell.id}
              padding="checkbox"
              sx={{
                width: headCell.width,
                p: '0 8px',
                verticalAlign: 'middle',
              }}
            >
              <Checkbox
                color="secondary"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{
                  'aria-label': 'Select All',
                }}
              />
            </TableCell>
          );
        }

        return (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sx={{
              fontFamily: 'Inter',
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              p: '0 16px',
              whiteSpace: 'nowrap',
              ...(headCell.width ? {
                width: headCell.width,
              } : {
                width: '10%',
              }),
            }}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ display: 'inline-flex' }}
            >
              {headCell.sortable ? (
                <TableSortLabel
                  active={isActive}
                  direction={isActive ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {headCell.tooltip && (
                  <Tooltip title={headCell.tooltip} />
                  )}
                </TableSortLabel>
              ) : (
                <>
                  {headCell.label}
                  {headCell.tooltip && (
                  <Tooltip title={headCell.tooltip} />
                  )}
                </>
              )}
            </Stack>
          </TableCell>
        );
      })}
    </TableRow>
  </MuiTableHead>
);

TableHead.propTypes = {
  headCells: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    numeric: PropTypes.bool,
    label: PropTypes.string,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    width: PropTypes.string,
    bold: PropTypes.bool,
    sortable: PropTypes.bool, // Indicates if the column is sortable
    isCheckbox: PropTypes.bool,
  })).isRequired,
  sortSelect: PropTypes.element,
  pagination: PropTypes.element,
  stickyHeader: PropTypes.bool,
  backgroundColor: PropTypes.string,
  order: PropTypes.oneOf(['asc', 'desc']),
  orderBy: PropTypes.string,
  onRequestSort: PropTypes.func,
  numSelected: PropTypes.number,
  rowCount: PropTypes.number,
  onSelectAllClick: PropTypes.func,
};

TableHead.defaultProps = {
  sortSelect: null,
  pagination: null,
  stickyHeader: false,
  backgroundColor: null,
  order: 'asc',
  orderBy: '',
  onRequestSort: () => {},
  numSelected: 0,
  rowCount: 0,
  onSelectAllClick: () => {},
};

export default TableHead;
